import { useState, useEffect } from 'react';
import { useCustomerUpdateFlag } from '@agria/paws/src/hooks/entra/useCustomerUpdateFlag';
import {
  breederLinks,
  customerLinks,
  nonAffiliateVetLinks,
  rehomingLinks,
  vetLinks,
} from '../const/navigationItems';
import {
  NavItem,
  User,
  CustomerUser,
  MergedAffiliateUser,
} from '../types/types';

/**
 * ========================================
 * NAVIGATION MANAGEMENT - FINAL STEPS
 * ========================================
 *
 * This hook handles the final steps of the authentication flow:
 * STEP 13: Set appropriate navigation based on user type
 * STEP 14: Send analytics flags for tracking
 *
 * This is triggered when UserDataManager completes data fetching
 * and updates customer/affiliate state.
 */
export const useNavigationManager = (
  user: User | null,
  customer: CustomerUser | null,
  affiliate: MergedAffiliateUser | null
) => {
  const [navItems, setNavItems] = useState<NavItem[]>([]);
  const { mutate: sendCustomerFlag } = useCustomerUpdateFlag();

  // STEP 14: Send customer analytics flag when customer user is available
  useEffect(() => {
    if (user?.CustomerId) {
      // STEP 14: Sending customer analytics flag for tracking
      sendCustomerFlag({
        customerId: user.CustomerId,
        flagId: '11388D3A-0257-4A55-B8BC-C67B7B237077',
      });
    }
  }, [user?.CustomerId, sendCustomerFlag]);

  // STEP 13: Update navigation based on user type and category
  useEffect(() => {
    if (customer) {
      // STEP 13A: Setting customer navigation
      setNavItems(customerLinks);
    } else if (affiliate?.AffiliateCategory) {
      // STEP 13B: Setting affiliate navigation for category

      // Set navigation based on affiliate category
      switch (affiliate.AffiliateCategory) {
        case 'Non-affiliate Vet':
          setNavItems(nonAffiliateVetLinks);
          break;
        case 'Vet':
          setNavItems(vetLinks);
          break;
        case 'Charity':
        case 'Rehoming':
          setNavItems(rehomingLinks);
          break;
        case 'Breeder':
          setNavItems(breederLinks);
          break;
        default:
          // Unknown affiliate category, setting empty navigation
          setNavItems([]);
      }
      // STEP 13B: Navigation updated for affiliate category
    } else {
      // STEP 13C: No user data available, clearing navigation
      setNavItems([]);
    }

    // STEP 13: Navigation setup complete - User can now interact with dashboard
  }, [customer, affiliate]);

  return navItems;
};
